import clsx from "clsx";
import { Formik, FormikProps } from "formik";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { userJobSchema } from "./schema/userJob";
import { Input } from "@/components/form/Input";
import { InputError } from "@/components/form/InputError";
import { TextArea } from "@/components/form/Textarea";
import { IUserJob } from "@/types";

interface UserJobFormProps {
    userJob?: IUserJob;
    onSave: (userJob: IUserJob) => void;
}

export interface UserJobFormRef {
    submitForm: () => void;
}

export const UserJobForm = forwardRef<UserJobFormRef, UserJobFormProps>(
    ({ userJob, onSave }, ref) => {
        const formikRef = useRef<FormikProps<IUserJob>>(null);

        useImperativeHandle(ref, () => ({
            submitForm: () => {
                formikRef.current?.handleSubmit();
            },
        }));

        const initialValues: Partial<IUserJob> = useMemo(
            () => ({
                id: userJob?.id || "new",
                jobTitle: userJob?.jobTitle || "",
                jobDescription: userJob?.jobDescription || "",
                companyName: userJob?.companyName || "",
                location: userJob?.location || "",
                jobPostingUrl: userJob?.jobPostingUrl || "",
                linkedResumeIds: userJob?.linkedResumeIds || [],
                status: userJob?.status || "Applying",
            }),
            [userJob],
        );

        const onSubmit = async (values: IUserJob) => {
            onSave(values);
        };

        return (
            <Formik
                innerRef={formikRef}
                initialValues={initialValues as IUserJob}
                validationSchema={userJobSchema}
                onSubmit={onSubmit}
                validateOnBlur={false}
                validateOnMount={false}
                validateOnChange={false}
            >
                {({
                    values,
                    errors,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                }) => {
                    return (
                        <form
                            onSubmit={handleSubmit}
                            className="space-y-4"
                        >
                            <div className="flex w-full flex-col gap-4 lg:flex-row">
                                <div className="w-full lg:w-1/2">
                                    <Input
                                        label="Job Title/Role"
                                        name="jobTitle"
                                        value={values.jobTitle}
                                        formikChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.jobTitle}
                                        required
                                    />
                                </div>
                                <div className="w-full lg:w-1/2">
                                    <Input
                                        label="Company Name"
                                        name="companyName"
                                        value={values.companyName}
                                        formikChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.companyName}
                                    />
                                </div>
                            </div>
                            <div className="flex w-full flex-col gap-4 lg:flex-row">
                                <div className="w-full lg:w-1/2">
                                    <Input
                                        label="Link to Job Listing"
                                        name="jobPostingUrl"
                                        value={values.jobPostingUrl}
                                        formikChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.jobPostingUrl}
                                    />
                                </div>
                                <div className="w-full lg:w-1/2">
                                    <Input
                                        label="Location"
                                        name="location"
                                        value={values.location}
                                        formikChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.location}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <TextArea
                                    id="jobDescription"
                                    label="Job Description"
                                    name="jobDescription"
                                    value={values.jobDescription}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                    className={clsx(
                                        "h-32 w-full bg-neutral-100 !font-normal text-neutral-600",
                                        errors.jobDescription
                                            ? "ring-2 ring-error-500"
                                            : "ring-1 ring-neutral-100",
                                    )}
                                    showCounter={
                                        values.jobDescription.length >= 9000
                                    }
                                    maxLength={10000}
                                    placeholder="Paste the job descriptions, requirements and responsibilities here..."
                                    minLength={80}
                                    error={errors.jobDescription}
                                />
                                <InputError error={errors.jobDescription} />
                            </div>
                        </form>
                    );
                }}
            </Formik>
        );
    },
);

UserJobForm.displayName = "UserJobForm";
